import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Input, InputRef, Modal, Select, Switch, Tag, Tooltip, message } from "antd";
import { CloseOutlined, DeleteOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { useRecoilValue } from "recoil";
import { locationsState } from "../../../../state/atoms";
import DataKitApi from "../../../../api/DataKitApi";
import {
  DataKitDto,
  DataKitGroupDto,
  DataKitItemDto,
  DataKitItemGroupDto,
  DataKitItemModifierDto,
  DataKitItemModifierGroupDto,
  DataKitItemPriceDto,
  MenuSummaryDto,
} from "../../../../domain/DataKitDto";
import { DataKitOverrideTables } from "./DataKitOverrideTables";
import "./DataKitDrawer.scss";
import "./DataKitItemDrawer.scss";
import { useDrawerDismiss } from "./useDrawerDismiss";
import { DataKitUsedOnLink } from "./DataKitMenuView";

export function blankDataKitItem(): DataKitItemDto {
  return {
    name: "",
    description: "",
    sequence: 0,
    archived: false,
    available: true,
    prices: [{ size: "", price: "", sequence: 0, overrides: [] }],
    tags: [],
    groups: [],
    modifierGroups: [],
    usedOnCount: 0,
  };
}

// Unit Separator (U+001F), built from a char code rather than embedded as a raw control
// character: a literal one is invisible in diffs and easy for tooling to mangle. Chosen
// because it cannot appear in a group name - a readable separator like " / " is a name a
// member could legitimately type, which would make two different nested paths encode alike.
const PATH_SEP = String.fromCharCode(31);

const pathOf = (entry: DataKitItemGroupDto) =>
  entry.groupPath && entry.groupPath.length > 0 ? entry.groupPath : [entry.groupName];

/** Stable key for a group entry: its full path, which identifies a group once groups nest. */
const entryKey = (entry: DataKitItemGroupDto) => pathOf(entry).join(PATH_SEP);

interface Props {
  kit: DataKitDto;
  item: DataKitItemDto;
  groups: DataKitGroupDto[];
  /** The menus (designs) using this library - the menu price picker's other menus. */
  menus: MenuSummaryDto[];
  canWrite: boolean;
  onClose: () => void;
  onSaved: () => void;
  /**
   * Save and add another: the item was saved and the drawer is staying open for the next one.
   * The parent refreshes its list without closing - see DataKitDetailPage.
   */
  onAdded?: () => void;
  onStructureChanged: () => void;
}

export function DataKitItemDrawer({
  kit,
  item: initialItem,
  groups,
  menus,
  canWrite,
  onClose,
  onSaved,
  onAdded,
  onStructureChanged,
}: Props) {
  const locations = useRecoilValue(locationsState);

  const [item, setItem] = useState(initialItem);
  const [name, setName] = useState(item.name);
  const [description, setDescription] = useState(item.description);
  const [prices, setPrices] = useState<DataKitItemPriceDto[]>(
    item.prices.length > 0
      ? [...item.prices].sort((a, b) => a.sequence - b.sequence)
      : [{ size: "", price: "", sequence: 0, overrides: [] }]
  );
  const [showSizeLabels, setShowSizeLabels] = useState(
    item.prices.some((p) => (p.size || "").trim() !== "")
  );
  const [extras, setExtras] = useState(item.extras || "");
  // Sorted in, resequenced out (see `save`): the server stores an explicit order and returns it
  // unsorted, so the row order the member sees has to be established here or it drifts per load.
  const [modifierGroups, setModifierGroups] = useState<DataKitItemModifierGroupDto[]>(() =>
    [...(item.modifierGroups || [])]
      .sort((a, b) => a.sequence - b.sequence)
      .map((g) => ({
        ...g,
        modifiers: [...(g.modifiers || [])].sort((a, b) => a.sequence - b.sequence),
      }))
  );
  const [tags, setTags] = useState<string[]>([...item.tags]);
  const [tagDraft, setTagDraft] = useState("");
  // Dietary / Allergen labels, apart from tags since Oct 14 - text for now, icons later.
  const [dietary, setDietary] = useState<string[]>([...(item.dietary || [])]);
  const [dietaryDraft, setDietaryDraft] = useState("");
  const [available, setAvailable] = useState(item.available);
  const [entries, setEntries] = useState<DataKitItemGroupDto[]>(
    item.groups.map((g) => ({
      groupName: g.groupName,
      groupPath: g.groupPath && g.groupPath.length > 0 ? g.groupPath : [g.groupName],
    }))
  );
  const [draftGroup, setDraftGroup] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);

  const [newGroupOpen, setNewGroupOpen] = useState(false);
  // Which existing group the new one is created under. Empty = top level. One control covers both
  // "new group" and "new subgroup" now that a subgroup is just a nested group.
  const [newGroupParent, setNewGroupParent] = useState<string>("");
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  const isNew = !item.id;
  const nameInput = useRef<InputRef>(null);

  const { closing, dismiss } = useDrawerDismiss();
  const close = useCallback(() => dismiss(onClose), [dismiss, onClose]);

  // The shortcut reads the latest saveAndAddAnother through a ref, so the listener doesn't have to
  // be torn down and re-bound on every keystroke the form takes.
  const shortcut = useRef<() => void>(() => undefined);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !newGroupOpen) close();
      // Cmd/Ctrl+Enter: save and add another (the AC's nice-to-have).
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && !newGroupOpen) {
        e.preventDefault();
        shortcut.current();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [close, newGroupOpen]);

  /** Every group in the kit, labelled by full path - one picker covers any depth. */
  const groupOptions = useMemo(
    () =>
      groups
        .map((g) => {
          const path = g.path && g.path.length > 0 ? g.path : [g.name];
          return { label: path.join(" › "), value: path.join(PATH_SEP) };
        })
        .sort((a, b) => a.label.localeCompare(b.label)),
    [groups]
  );

  const updatePrice = (index: number, patch: Partial<DataKitItemPriceDto>) => {
    setPrices((current) => current.map((p, i) => (i === index ? { ...p, ...patch } : p)));
  };

  const addPrice = () => {
    setPrices((current) => [
      ...current,
      { size: "", price: "", sequence: current.length, overrides: [], menuOverrides: [] },
    ]);
  };

  const removePrice = (index: number) => {
    setPrices((current) =>
      current.filter((_, i) => i !== index).map((p, i) => ({ ...p, sequence: i }))
    );
  };

  /*
   * Modifier groups. Storage only in v1 - they are held for Item Library field parity and are
   * deliberately never printed on the canvas (see DataKitItemModifierGroup's KDoc in `shared`), so
   * there is no canvas-side counterpart to keep in step with any of this.
   *
   * Sequence is not tracked while editing: `save` renumbers both levels from array order, so these
   * only have to keep the arrays in the order the member sees.
   */
  const patchGroup = (index: number, patch: Partial<DataKitItemModifierGroupDto>) => {
    setModifierGroups((current) => current.map((g, i) => (i === index ? { ...g, ...patch } : g)));
  };

  const addModifierGroup = () => {
    setModifierGroups((current) => [
      ...current,
      { name: "", sequence: current.length, modifiers: [{ name: "", price: "", sequence: 0 }] },
    ]);
  };

  const removeModifierGroup = (index: number) => {
    setModifierGroups((current) => current.filter((_, i) => i !== index));
  };

  const addModifier = (groupIndex: number) => {
    const group = modifierGroups[groupIndex];
    patchGroup(groupIndex, {
      modifiers: [...group.modifiers, { name: "", price: "", sequence: group.modifiers.length }],
    });
  };

  const updateModifier = (groupIndex: number, index: number, patch: Partial<DataKitItemModifierDto>) => {
    const group = modifierGroups[groupIndex];
    patchGroup(groupIndex, {
      modifiers: group.modifiers.map((m, i) => (i === index ? { ...m, ...patch } : m)),
    });
  };

  const removeModifier = (groupIndex: number, index: number) => {
    const group = modifierGroups[groupIndex];
    patchGroup(groupIndex, { modifiers: group.modifiers.filter((_, i) => i !== index) });
  };

  const commitTag = () => {
    const value = tagDraft.trim();
    if (!value) return;
    if (!tags.some((t) => t.toLowerCase() === value.toLowerCase())) {
      setTags((current) => [...current, value]);
    }
    setTagDraft("");
  };

  const commitDietary = () => {
    const value = dietaryDraft.trim();
    if (!value) return;
    if (!dietary.some((d) => d.toLowerCase() === value.toLowerCase())) {
      setDietary((current) => [...current, value]);
    }
    setDietaryDraft("");
  };

  const addEntry = (value?: string) => {
    const picked = value ?? draftGroup;
    if (!picked) return;
    const path = picked.split(PATH_SEP);
    const next: DataKitItemGroupDto = { groupName: path[path.length - 1], groupPath: path };
    setDraftGroup(undefined);
    if (entries.some((e) => entryKey(e) === entryKey(next))) {
      message.info("This item is already filed there");
      return;
    }
    setEntries((current) => [...current, next]);
  };

  const removeEntry = (target: DataKitItemGroupDto) => {
    setEntries((current) => current.filter((e) => entryKey(e) !== entryKey(target)));
  };

  const createGroup = () => {
    const value = newName.trim();
    if (!value || !kit.id) return;
    setCreating(true);
    DataKitApi.createGroup(kit.id, { groupName: value, name: value, path: newGroupParent ? [...newGroupParent.split(PATH_SEP), value] : [value] })
      .then(() => {
        setNewGroupOpen(false);
        setNewName("");
        addEntry(newGroupParent ? [...newGroupParent.split(PATH_SEP), value].join(PATH_SEP) : value);
        onStructureChanged();
      })
      .catch(() => message.error("Could not create that group"))
      .finally(() => setCreating(false));
  };

  const canSave = name.trim() !== "" && entries.length > 0;

  /**
   * Back to an empty item for the next entry, keeping the groups.
   *
   * Entering items by hand is mostly entering items that belong together, so the group is the
   * one field worth carrying over (AC: "The group stays filled in for the next item"). Prices and
   * size labels clear with everything else - a second item rarely shares the first one's price,
   * and a carried-over price is the kind of default that gets saved by accident.
   */
  const resetForNext = () => {
    setItem(blankDataKitItem());
    setName("");
    setDescription("");
    setPrices([{ size: "", price: "", sequence: 0, overrides: [] }]);
    setShowSizeLabels(false);
    setExtras("");
    setTags([]);
    setTagDraft("");
    setDietary([]);
    setDietaryDraft("");
    setModifierGroups([]);
    setAvailable(true);
    // After React has committed the cleared value, or focus lands in a field about to re-render.
    window.setTimeout(() => nameInput.current?.focus(), 0);
  };

  const save = () => persist(false);
  const saveAndAddAnother = () => persist(true);
  shortcut.current = () => {
    if (canWrite && canSave && !saving) saveAndAddAnother();
  };

  const persist = (andAnother: boolean) => {
    if (!kit.id || !canSave) return;
    const cleanedPrices = prices
      .filter((p) => (p.price || "").trim() !== "" || (p.size || "").trim() !== "")
      .map((p, i) => ({
        ...p,
        size: showSizeLabels ? p.size : "",
        sequence: i,
        // A blank location cell inherits the library price, so it is not sent at all - shared
        // rejects an override with no price. A new location row is seeded with one blank cell, and
        // an admin who only fills its modifier prices leaves it that way.
        overrides: (p.overrides || []).filter((o) => (o.price || "").trim() !== ""),
      }));

    // A group with neither a name nor an option is a row the member started and abandoned, not
    // data - same rule the prices above already apply. Either one alone is kept: a group name with
    // no options ("Choice of side") is meaningful, and so are options with no group name (Oct 15,
    // QA: Timber allows them, and dropping them silently lost what was plainly on screen).
    const cleanedModifierGroups = modifierGroups
      .map((group) => ({
        ...group,
        name: group.name.trim(),
        modifiers: group.modifiers
          .map((m) => ({ ...m, name: m.name.trim(), price: (m.price || "").trim() || null }))
          .filter((m) => m.name !== "")
          .map((m, i) => ({ ...m, sequence: i })),
      }))
      .filter((group) => group.name !== "" || group.modifiers.length > 0)
      .map((group, i) => ({ ...group, sequence: i }));

    const payload: DataKitItemDto = {
      ...item,
      name: name.trim(),
      description: description.trim(),
      extras: extras.trim() || null,
      prices: cleanedPrices,
      tags,
      dietary,
      groups: entries,
      modifierGroups: cleanedModifierGroups,
    };

    setSaving(true);
    const request = isNew
      ? DataKitApi.createItem(kit.id, payload)
      : DataKitApi.updateItem(item.id as string, payload);

    request
      .then(() => {
        if (andAnother) {
          message.success(`${name.trim()} ${isNew ? "added" : "saved"}`);
          onAdded?.();
          resetForNext();
        } else {
          dismiss(onSaved);
        }
      })
      .catch(() => message.error("Could not save this item"))
      .finally(() => setSaving(false));
  };

  return (
    <>
      <div className={`data-kit-drawer-scrim${closing ? " closing" : ""}`} onClick={close} />
      <aside
        className={`data-kit-drawer${closing ? " closing" : ""}`}
        role="dialog"
        aria-label={name || "New item"}
      >
        <header className="data-kit-drawer-header">
          <h2>{isNew ? "New Item" : name || "Untitled"}</h2>
          <button
            type="button"
            className="data-kit-drawer-close"
            onClick={close}
            aria-label="Close"
          >
            <CloseOutlined />
          </button>
        </header>

        <div className="data-kit-drawer-body">
          <div className="data-kit-available-row">
            <span className="data-kit-available-label">
              Available
              <Tooltip title="Off hides this item from every menu and channel straight away - it does not wait for a publish.">
                <QuestionCircleOutlined className="data-kit-available-help" />
              </Tooltip>
            </span>
            <Switch
              className="data-kit-available-switch"
              size="small"
              checked={available}
              disabled={!canWrite || isNew}
              onChange={(checked) => {
                if (!kit.id || !item.id) return;
                setAvailable(checked);
                DataKitApi.setAvailability(kit.id, {
                  itemIds: [item.id],
                  available: checked,
                }).catch(() => {
                  setAvailable(!checked);
                  message.error("Could not change availability");
                });
              }}
            />
          </div>

          <label className="data-kit-drawer-field">
            <span>Item Name</span>
            <Input
              ref={nameInput}
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={!canWrite}
              maxLength={255}
              autoFocus
            />
          </label>

          <label className="data-kit-drawer-field">
            <span>Description</span>
            <Input.TextArea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={!canWrite}
              rows={3}
            />
          </label>

          <div className="data-kit-drawer-field">
            <span>Groups this item belongs to</span>
            {entries.length > 0 && (
              <div className="data-kit-entry-list">
                {entries.map((entry, i) => (
                  <span
                    key={entryKey(entry)}
                    className={`data-kit-entry${i === 0 ? " first" : ""}`}
                    title={i === 0 ? "Designs render this item under its first group" : undefined}
                  >
                    <span className="data-kit-entry-label">
                      {pathOf(entry).slice(0, -1).length > 0 && (
                        <span className="data-kit-entry-sub">
                          {`${pathOf(entry).slice(0, -1).join(" › ")} › `}
                        </span>
                      )}
                      {entry.groupName}
                    </span>
                    {canWrite && (
                      <button
                        type="button"
                        className="data-kit-entry-remove"
                        onClick={() => removeEntry(entry)}
                        aria-label={`Remove ${entry.groupName}`}
                      >
                        <CloseOutlined/>
                      </button>
                    )}
                  </span>
                ))}
              </div>
            )}
            {entries.length === 0 && (
              <span className="data-kit-drawer-error">Pick at least one group.</span>
            )}

            {canWrite && (
              <div className="data-kit-entry-add">
                <Select
                  className="data-kit-entry-select wide"
                  showSearch
                  placeholder="Add to a group"
                  value={draftGroup}
                  onChange={(value) => addEntry(value)}
                  optionFilterProp="label"
                  options={groupOptions}
                  getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
                />
              </div>
            )}

            {canWrite && (
              <div className="data-kit-entry-inline-new">
                <Button
                  className="secondary"
                  onClick={() => {
                    setNewName("");
                    setNewGroupOpen(true);
                  }}
                >
                  + New group
                </Button>
              </div>
            )}
          </div>

          <div className="data-kit-drawer-field">
            <span>Price</span>
            {prices.map((price, index) => (
              <div className="data-kit-price-row" key={index}>
                {showSizeLabels && (
                  <Input
                    className="data-kit-price-size"
                    placeholder="size"
                    value={price.size}
                    onChange={(e) => updatePrice(index, { size: e.target.value })}
                    disabled={!canWrite}
                    maxLength={255}
                  />
                )}
                <Input
                  className="data-kit-price-amount"
                  placeholder="price"
                  value={price.price}
                  onChange={(e) => updatePrice(index, { price: e.target.value })}
                  disabled={!canWrite}
                  maxLength={255}
                />
                {canWrite && prices.length > 1 && (
                  <button
                    type="button"
                    className="data-kit-price-remove"
                    onClick={() => removePrice(index)}
                    aria-label="Remove price"
                  >
                    <DeleteOutlined />
                  </button>
                )}
              </div>
            ))}
            {canWrite && (
              <div className="data-kit-price-buttons">
                {!showSizeLabels && (
                  <Button className="secondary" onClick={() => setShowSizeLabels(true)}>
                    + Size Label
                  </Button>
                )}
                <Button className="secondary" onClick={addPrice}>
                  + Price
                </Button>
              </div>
            )}
          </div>

          <div className="data-kit-drawer-bundle">
            <DataKitOverrideTables
              kit={kit}
              item={item}
              prices={prices}
              modifierGroups={modifierGroups}
              onModifierGroupsChanged={setModifierGroups}
              locations={locations}
              menus={menus}
              canWrite={canWrite}
              onItemChanged={(updated) =>
                setPrices([...updated.prices].sort((a, b) => a.sequence - b.sequence))
              }
              onLocationOverridesChanged={setPrices}
            />
          </div>

          <label className="data-kit-drawer-field">
            <span>Extras</span>
            <Input
              placeholder="Extras"
              value={extras}
              onChange={(e) => setExtras(e.target.value)}
              disabled={!canWrite}
              maxLength={255}
            />
          </label>

          <div className="data-kit-drawer-field">
            <span>Modifier groups</span>
            {modifierGroups.map((group, groupIndex) => (
              <div className="data-kit-modifier-group" key={group.id || `new-${groupIndex}`}>
                <div className="data-kit-modifier-group-header">
                  <Input
                    className="data-kit-modifier-group-name"
                    placeholder="e.g. Choice of side"
                    value={group.name}
                    onChange={(e) => patchGroup(groupIndex, { name: e.target.value })}
                    disabled={!canWrite}
                    maxLength={255}
                  />
                  {canWrite && (
                    <button
                      type="button"
                      className="data-kit-price-remove"
                      onClick={() => removeModifierGroup(groupIndex)}
                      aria-label={`Remove ${group.name || "modifier group"}`}
                    >
                      <DeleteOutlined />
                    </button>
                  )}
                </div>

                {group.modifiers.map((modifier, index) => (
                  <div className="data-kit-modifier-row" key={modifier.id || `new-${index}`}>
                    <Input
                      className="data-kit-modifier-name"
                      placeholder="modifier"
                      value={modifier.name}
                      onChange={(e) =>
                        updateModifier(groupIndex, index, { name: e.target.value })
                      }
                      disabled={!canWrite}
                      maxLength={255}
                    />
                    <Input
                      className="data-kit-modifier-price"
                      placeholder="&mdash;"
                      value={modifier.price || ""}
                      onChange={(e) =>
                        updateModifier(groupIndex, index, { price: e.target.value })
                      }
                      disabled={!canWrite}
                      maxLength={255}
                    />
                    {canWrite && (
                      <button
                        type="button"
                        className="data-kit-price-remove"
                        onClick={() => removeModifier(groupIndex, index)}
                        aria-label="Remove modifier"
                      >
                        <DeleteOutlined />
                      </button>
                    )}
                  </div>
                ))}

                {canWrite && (
                  <div className="data-kit-entry-inline-new">
                    <Button className="secondary" onClick={() => addModifier(groupIndex)}>
                      + Modifier
                    </Button>
                  </div>
                )}
              </div>
            ))}
            {canWrite && (
              <div className="data-kit-entry-inline-new">
                <Button className="secondary"  onClick={addModifierGroup}>
                  + Modifier group
                </Button>
              </div>
            )}
            <p className="data-kit-modifier-note">
              Modifier prices can differ by location (below) and by menu &mdash; menu prices are set
              in the editor.
            </p>
          </div>

          <div className="data-kit-drawer-field">
            <span>Tags</span>
            {tags.length > 0 && (
              <div className="data-kit-tag-list">
                {tags.map((tag) => (
                  <Tag
                    key={tag}
                    className="data-kit-tag"
                    closable={canWrite}
                    onClose={() => setTags((current) => current.filter((t) => t !== tag))}
                  >
                    {tag}
                  </Tag>
                ))}
              </div>
            )}
            <Input
              placeholder="Tags"
              value={tagDraft}
              onChange={(e) => setTagDraft(e.target.value)}
              onPressEnter={commitTag}
              onBlur={commitTag}
              disabled={!canWrite}
              maxLength={255}
            />
          </div>

          {/* Below Tags, as text (Oct 14, QA) - these become icons once dietary icons exist. */}
          <div className="data-kit-drawer-field">
            <span>Dietary / Allergen</span>
            {dietary.length > 0 && (
              <div className="data-kit-tag-list">
                {dietary.map((label) => (
                  <Tag
                    key={label}
                    className="data-kit-tag dietary"
                    closable={canWrite}
                    onClose={() => setDietary((current) => current.filter((d) => d !== label))}
                  >
                    {label}
                  </Tag>
                ))}
              </div>
            )}
            <Input
              placeholder="Dietary / Allergen"
              value={dietaryDraft}
              onChange={(e) => setDietaryDraft(e.target.value)}
              onPressEnter={commitDietary}
              onBlur={commitDietary}
              disabled={!canWrite}
              maxLength={255}
            />
          </div>
        </div>

        <footer className="data-kit-drawer-footer">
          {/* No Cancel: the header X closes, exactly as Cancel did (Lyra, Oct 1 - removed from every
              drawer footer for consistency). */}
          <div className="data-kit-drawer-footer-buttons">
            <Button
              className="data-kit-drawer-save-another"
              onClick={saveAndAddAnother}
              disabled={!canWrite || !canSave || saving}
              title={`Save and add another (${navigator.platform.includes("Mac") ? "\u2318" : "Ctrl"}+Enter)`}
            >
              Save and add another
            </Button>
            <Button
              type="primary"
              className="data-kit-drawer-save"
              onClick={save}
              loading={saving}
              disabled={!canWrite || !canSave}
            >
              Save
            </Button>
          </div>
          <p className="data-kit-drawer-footer-note">
            {item.usedOnCount > 0 ? (
              <>
                <DataKitUsedOnLink
                  itemId={item.id}
                  itemName={item.name}
                  count={item.usedOnCount}
                  variant="sentence"
                />{" "}
                - saving reaches every design
              </>
            ) : (
              <>Not used on any design yet</>
            )}
          </p>
        </footer>
      </aside>

      <Modal
        className={"clean one"}
        open={newGroupOpen}
        onCancel={() => setNewGroupOpen(false)}
      >
        <header>
          <h2>New group</h2>
        </header>
        <Input
          size="large"
          placeholder="e.g. Prime Seafood"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onPressEnter={createGroup}
          maxLength={255}
          autoFocus
        />
        <label className="data-kit-drawer-field" style={{ marginTop: 12 }}>
          <span>Inside</span>
          <Select
            className="data-kit-entry-select wide"
            showSearch
            allowClear
            placeholder="Top level"
            value={newGroupParent || undefined}
            onChange={(value) => setNewGroupParent(value || "")}
            optionFilterProp="label"
            options={groupOptions}
          />
        </label>

        <footer>
          <Button size="large" onClick={() => setNewGroupOpen(false)}>
            Cancel
          </Button>
          <Button
            size="large"
            className="primary"
            loading={creating}
            disabled={!newName.trim()}
            onClick={createGroup}
          >
            Create
          </Button>
        </footer>
      </Modal>
    </>
  );
}
